import { SentIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  listDashPhoneVersions,
  PHONE_RUNTIME_LATEST,
} from "../../case-spec";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  formatOpapiFailure,
  formatPublishFailureDetail,
  type CaseVersion,
  type PublishCaseResult,
  type PublishGateResult,
} from "@/lib/api";
import {
  publishedLocalesFromStatus,
  resolvePublishVersionIds,
} from "@/lib/publish-targets";
import { formatReadyGateIssueLine } from "@/lib/ready-gate-copy";
import { formatVersionStatus, statusBadgeVariant } from "@/lib/case-format";
import { cn } from "@/lib/utils";

const PHONE_PIN_OPTIONS = [
  ...listDashPhoneVersions(),
  PHONE_RUNTIME_LATEST,
] as const;

const pinSelectClassName = cn(
  "h-7 w-full max-w-xs rounded-md border border-input bg-input/20 px-2 py-0.5 text-sm outline-none",
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
  "disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
);

type CasePublishPanelProps = {
  caseId: string;
  versions: CaseVersion[];
  /** Version used for pin editor and preferred publish target (usually the open version). */
  sourceVersion: CaseVersion | null;
  /**
   * When true, prefer sourceVersion for its locale on publish.
   * Other selected locales still resolve to their best phone package.
   */
  singleVersion?: boolean;
};

function gateSummary(gate: PublishGateResult | undefined): string {
  if (!gate) return "未检查";
  if (gate.ok) return "通过";
  const parts: string[] = [];
  if (!gate.validateOk) parts.push("校验");
  if (gate.placeholderCount > 0) {
    parts.push(`${gate.placeholderCount} 个占位符`);
  }
  if (!gate.mediaOk) parts.push("媒体");
  if (gate.phoneReleasePresent === false) parts.push("手机发行版");
  return parts.length ? parts.join(", ") : "已拦截";
}

function defaultSelectedLocales(
  localesWithPhone: string[],
  sourceLocale?: string,
): string[] {
  if (sourceLocale && localesWithPhone.includes(sourceLocale)) {
    return [sourceLocale];
  }
  return [...localesWithPhone];
}

export function CasePublishPanel({
  caseId,
  versions,
  sourceVersion,
  singleVersion = false,
}: CasePublishPanelProps) {
  const queryClient = useQueryClient();
  const [selectedLocales, setSelectedLocales] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [forcePublish, setForcePublish] = useState(false);
  const [publishAsLatest, setPublishAsLatest] = useState(false);
  const [pinDraft, setPinDraft] = useState(
    sourceVersion?.phoneRuntimeVersion ?? "",
  );
  const [lastPublish, setLastPublish] = useState<PublishCaseResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [selectedUnpublishLocales, setSelectedUnpublishLocales] = useState<
    string[]
  >([]);

  const publishQuery = useQuery({
    queryKey: ["case-publish", caseId],
    queryFn: () => api.getCasePublish(caseId),
  });

  const groupsQuery = useQuery({
    queryKey: ["user-groups"],
    queryFn: () => api.listUserGroups(),
  });

  const sourceVersionId = sourceVersion?.id ?? null;
  const readyQuery = useQuery({
    queryKey: ["publish-gate", sourceVersionId],
    queryFn: () => api.getPublishGate(sourceVersionId!),
    enabled: Boolean(sourceVersionId),
    refetchInterval: 5000,
  });

  useEffect(() => {
    setPinDraft(sourceVersion?.phoneRuntimeVersion ?? "");
  }, [sourceVersion?.id, sourceVersion?.phoneRuntimeVersion]);

  useEffect(() => {
    if (sourceVersion?.phoneRuntimeVersion !== PHONE_RUNTIME_LATEST) {
      setPublishAsLatest(false);
    }
  }, [sourceVersion?.phoneRuntimeVersion]);

  const localesWithPhone = useMemo(() => {
    return versions
      .filter((v) => v.phoneJsonR2Key)
      .map((v) => v.locale)
      .filter((loc, i, arr) => arr.indexOf(loc) === i)
      .sort();
  }, [versions]);

  const publishLocales = publishQuery.data?.locales ?? [];

  const pinDiffLocales = useMemo(() => {
    return publishLocales.filter((loc) => {
      if (!loc.publishedVersionId) return false;
      if (!loc.d1PhoneRuntimeVersion || !loc.publishedPhoneRuntimeVersion) {
        return false;
      }
      return loc.d1PhoneRuntimeVersion !== loc.publishedPhoneRuntimeVersion;
    });
  }, [publishLocales]);

  const localeMetaByCode = useMemo(() => {
    const map = new Map(publishLocales.map((loc) => [loc.locale, loc]));
    return map;
  }, [publishLocales]);

  const liveLocales = useMemo(
    () => publishedLocalesFromStatus(publishLocales),
    [publishLocales],
  );

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["case", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["case-publish", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["case-preview", caseId] }),
      queryClient.invalidateQueries({ queryKey: ["publish-gate", sourceVersionId] }),
      queryClient.invalidateQueries({
        queryKey: ["media-readiness", sourceVersionId],
      }),
    ]);
  };

  const userGroups = groupsQuery.data?.groups ?? [];

  const selectedGroupUidCount = useMemo(() => {
    if (selectedGroupIds.length === 0) return 0;
    const set = new Set<number>();
    for (const group of userGroups) {
      if (!selectedGroupIds.includes(group.id)) continue;
      for (const uid of group.uids) set.add(uid);
    }
    return set.size;
  }, [selectedGroupIds, userGroups]);

  const resetPublishDialogOptions = () => {
    setForcePublish(false);
    setPublishAsLatest(false);
    setSelectedGroupIds([]);
  };

  const openPublishDialog = () => {
    setSelectedLocales(
      defaultSelectedLocales(localesWithPhone, sourceVersion?.locale),
    );
    resetPublishDialogOptions();
    setPublishOpen(true);
  };

  const openUnpublishDialog = () => {
    const liveCodes = liveLocales.map((l) => l.locale);
    if (
      sourceVersion?.locale &&
      liveCodes.includes(sourceVersion.locale)
    ) {
      setSelectedUnpublishLocales([sourceVersion.locale]);
    } else {
      setSelectedUnpublishLocales([...liveCodes]);
    }
    setUnpublishOpen(true);
  };

  const pinMutation = useMutation({
    mutationFn: (pin: string) =>
      api.updateVersionPhoneRuntime(sourceVersionId!, pin),
    onSuccess: async (result) => {
      setError(null);
      setPinDraft(result.to);
      toast.add({
        type: "success",
        title: "手机运行时 pin 已更新",
        description: `${result.from} → ${result.to}。预览使用 D1 pin。`,
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "pin_update_failed";
      setError(code);
      toast.add({
        type: "error",
        title: "Pin 更新失败",
        description: code,
        priority: "high",
      });
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => {
      const preferred = singleVersion ? sourceVersion : null;
      const versionIds = resolvePublishVersionIds(
        selectedLocales,
        versions,
        preferred,
      );
      if (versionIds.length === 0) {
        return Promise.reject(
          new ApiError(400, "no_publish_targets", "没有可发布的包"),
        );
      }
      return api.publishCase(caseId, {
        versionIds,
        force: forcePublish || undefined,
        publishAsLatest: publishAsLatest || undefined,
        groupIds: selectedGroupIds,
      });
    },
    onSuccess: async (result) => {
      setLastPublish(result);
      setError(null);
      setPublishOpen(false);
      resetPublishDialogOptions();
      const audienceNote =
        result.uids && result.uids.length > 0
          ? `定向 ${result.uids.length} 个 UID。`
          : "全量发布。";
      toast.add({
        type: result.ok ? "success" : "error",
        title: result.ok ? "已发布" : "发布失败",
        description: result.ok
          ? `已通过 opapi 发布 ${result.published.length} 个语言。${audienceNote}`
          : formatOpapiFailure(result.opapi),
        priority: result.ok ? undefined : "high",
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "publish_failed";
      const toastDetail = formatPublishFailureDetail(
        code,
        err instanceof ApiError ? err.note : undefined,
      );
      setError(toastDetail);
      toast.add({
        type: "error",
        title:
          code === "publish_gate_failed"
            ? "发布检查未通过"
            : "发布失败",
        description: toastDetail,
        priority: "high",
      });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: () => {
      const versionIds = selectedUnpublishLocales
        .map(
          (locale) =>
            liveLocales.find((l) => l.locale === locale)?.versionId,
        )
        .filter((id): id is string => Boolean(id));
      if (versionIds.length === 0) {
        return Promise.reject(
          new ApiError(400, "version_id_required", "未选择语言"),
        );
      }
      return api.unpublishCase(caseId, { versionIds });
    },
    onSuccess: async (result) => {
      setError(null);
      setUnpublishOpen(false);
      const locales = result.unpublished.map((u) => u.locale).join(", ");
      toast.add({
        type: "success",
        title: "已取消发布",
        description: `已从生产环境和 OPAPI 移除 ${result.unpublished.length} 个语言：${locales}。`,
      });
      await invalidateAll();
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : "unpublish_failed";
      const detail =
        err instanceof ApiError && err.note ? `${code}: ${err.note}` : code;
      setUnpublishOpen(false);
      setError(detail);
      toast.add({
        type: "error",
        title: "取消发布失败",
        description: detail,
        priority: "high",
      });
    },
  });

  const gate = readyQuery.data;
  const sourcePin = sourceVersion?.phoneRuntimeVersion?.trim() ?? "";
  const sourcePinIsLatest = sourcePin === PHONE_RUNTIME_LATEST;
  const pinDirty =
    Boolean(sourceVersionId) &&
    pinDraft.trim() !== "" &&
    pinDraft.trim() !== sourcePin;
  const busy =
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    pinMutation.isPending;

  const publishConfirmDisabled =
    busy ||
    !publishQuery.data?.opapiConfigured ||
    selectedLocales.length === 0;

  const openPublishDisabled =
    busy ||
    !publishQuery.data?.opapiConfigured ||
    localesWithPhone.length === 0;

  const unpublishConfirmDisabled =
    busy ||
    !publishQuery.data?.opapiConfigured ||
    selectedUnpublishLocales.length === 0;

  const openUnpublishDisabled =
    busy ||
    !publishQuery.data?.opapiConfigured ||
    liveLocales.length === 0;

  const toggleLocale = (
    list: string[],
    locale: string,
    checked: boolean,
  ): string[] => {
    if (checked) {
      if (list.includes(locale)) return list;
      return [...list, locale].sort();
    }
    return list.filter((loc) => loc !== locale);
  };

  const headerAction =
    liveLocales.length > 0 || localesWithPhone.length > 0 ? (
      <div className="flex flex-wrap items-center gap-2">
        {liveLocales.length > 0 ? (
          <Button
            variant="outline"
            disabled={openUnpublishDisabled}
            onClick={openUnpublishDialog}
          >
            {unpublishMutation.isPending ? <Spinner /> : null}
            取消发布
          </Button>
        ) : null}
        {localesWithPhone.length > 0 ? (
          <Button disabled={openPublishDisabled} onClick={openPublishDialog}>
            <HugeiconsIcon icon={SentIcon} data-icon="inline-start" />
            发布
          </Button>
        ) : null}
      </div>
    ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>发布</CardTitle>
        <CardDescription>
          查看各语言状态与手机运行时 pin，再点发布选择语言并上线。发布会跑校验与媒体检查、写入
          pin，并通过 opapi 推送稳定 R2 URL。在翻译页创建语言。SPA 不会拿到
          OPAPI_KEY。
        </CardDescription>
        {headerAction ? <CardAction>{headerAction}</CardAction> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {!sourceVersion ? (
          <p className="text-sm text-muted-foreground">
            还没有 phone JSON 版本。请先完成流水线。
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium">源版本</p>
                <Badge variant="secondary">
                  {sourceVersion.locale} · v{sourceVersion.versionN}
                </Badge>
                <Badge variant={statusBadgeVariant(sourceVersion.status)}>
                  {sourceVersion.status}
                </Badge>
                <Badge variant="outline">
                  pin {sourceVersion.phoneRuntimeVersion}
                </Badge>
                {gate ? (
                  <Badge variant={gate.ok ? "success" : "warning"}>
                    门禁: {gateSummary(gate)}
                  </Badge>
                ) : null}
              </div>
              {gate && !gate.ok && gate.issues.length > 0 ? (
                <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-xs text-muted-foreground">
                  {gate.issues.map((issue) => (
                    <li key={`${issue.path}:${issue.reason}`}>
                      {formatReadyGateIssueLine(issue)}
                    </li>
                  ))}
                </ul>
              ) : null}

              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="phone-runtime-pin">
                    手机运行时 pin（D1）
                  </FieldLabel>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      id="phone-runtime-pin"
                      className={pinSelectClassName}
                      value={pinDraft}
                      disabled={busy || !sourceVersionId}
                      onChange={(e) => setPinDraft(e.target.value)}
                    >
                      {PHONE_PIN_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                      {pinDraft &&
                      !PHONE_PIN_OPTIONS.includes(
                        pinDraft as (typeof PHONE_PIN_OPTIONS)[number],
                      ) ? (
                        <option value={pinDraft}>{pinDraft}（当前）</option>
                      ) : null}
                    </select>
                    <Button
                      variant="outline"
                      disabled={busy || !sourceVersionId || !pinDirty}
                      onClick={() => pinMutation.mutate(pinDraft.trim())}
                    >
                      {pinMutation.isPending ? <Spinner /> : null}
                      保存 pin
                    </Button>
                  </div>
                  <FieldDescription>
                    运营用 pin，用于校验、发布写入与预览。预览 iframe 通过{" "}
                    <code className="text-xs">buildPhoneLoadUrl</code> 使用此 D1
                    值。具体 pin 必须存在于 R2 phone-releases。不会改写草稿
                    phone.json。
                  </FieldDescription>
                </Field>
              </FieldGroup>

              {sourcePinIsLatest ? (
                <Alert>
                  <AlertTitle>浮动 pin（latest）</AlertTitle>
                  <AlertDescription>
                    默认发布会把具体版本写入已发布 JSON，并保留 D1 为{" "}
                    <code className="text-xs">latest</code>
                    。仅当希望已发布正文也浮动时，才在发布对话框中打开「以
                    latest 发布」。重新固定 pin 后需再次发布以更新 CDN。
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          </>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">语言</p>
            {localesWithPhone.length === 0 ? (
              <FieldDescription>
                还没有带 phone JSON 的语言包。请先完成流水线（其他语言还需翻译）。
              </FieldDescription>
            ) : (
              <FieldDescription>
                各语言包状态。在发布对话框中选择要上线的语言。
              </FieldDescription>
            )}
          </div>

          {pinDiffLocales.length > 0 ? (
            <Alert>
              <AlertTitle>D1 pin 与已发布戳记不一致</AlertTitle>
              <AlertDescription>
                <ul className="mt-1 flex flex-col gap-1 text-xs">
                  {pinDiffLocales.map((loc) => (
                    <li key={loc.locale}>
                      <span className="font-medium">{loc.locale}</span>: D1{" "}
                      <code>{loc.d1PhoneRuntimeVersion}</code> vs 已发布{" "}
                      <code>{loc.publishedPhoneRuntimeVersion}</code>
                      {loc.d1PhoneRuntimeVersion === PHONE_RUNTIME_LATEST
                        ? "（默认发布时，浮动 D1 + 具体正文是预期行为）"
                        : "（重新发布以写入当前 D1 pin）"}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          {publishQuery.isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12" />
                  <Skeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          ) : publishLocales.length === 0 ? (
            <p className="text-sm text-muted-foreground">还没有语言。</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>语言</TableHead>
                    <TableHead>最新</TableHead>
                    <TableHead>已发布</TableHead>
                    <TableHead>D1 pin</TableHead>
                    <TableHead>已发布 pin</TableHead>
                    <TableHead>稳定 URL</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {publishLocales.map((loc) => {
                    const pinMismatch =
                      Boolean(loc.publishedVersionId) &&
                      Boolean(loc.d1PhoneRuntimeVersion) &&
                      Boolean(loc.publishedPhoneRuntimeVersion) &&
                      loc.d1PhoneRuntimeVersion !==
                        loc.publishedPhoneRuntimeVersion;
                    return (
                      <TableRow key={loc.locale}>
                        <TableCell className="font-medium">
                          {loc.locale}
                        </TableCell>
                        <TableCell>
                          {loc.latestVersionN != null ? (
                            <span className="text-sm">
                              v{loc.latestVersionN}{" "}
                              <Badge
                                variant={statusBadgeVariant(
                                  loc.latestStatus ?? "draft",
                                )}
                              >
                                {loc.latestStatus
                                  ? formatVersionStatus(loc.latestStatus)
                                  : "-"}
                              </Badge>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              -
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {loc.publishedVersionId ? (
                            <Badge variant="success">是</Badge>
                          ) : (
                            <Badge variant="outline">否</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {loc.d1PhoneRuntimeVersion ?? (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {loc.publishedPhoneRuntimeVersion ? (
                            <span
                              className={
                                pinMismatch
                                  ? "text-amber-700 dark:text-amber-400"
                                  : undefined
                              }
                            >
                              {loc.publishedPhoneRuntimeVersion}
                              {pinMismatch ? "（差异）" : ""}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[16rem] truncate font-mono text-xs text-muted-foreground">
                          {loc.publishedUrl}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {lastPublish ? (
          <Alert variant={lastPublish.ok ? "default" : "destructive"}>
            <AlertTitle>
              {lastPublish.ok ? "发布成功" : "发布失败"}
            </AlertTitle>
            <AlertDescription>
              <div className="flex flex-col gap-2 font-mono text-xs">
                <p>urls: {lastPublish.urls.join(", ") || "（无）"}</p>
                {lastPublish.published.length > 0 ? (
                  <p>
                    published:{" "}
                    {lastPublish.published
                      .map((p) => {
                        const stamp =
                          p.phoneRuntimeVersion != null
                            ? ` pin=${p.phoneRuntimeVersion}${
                                p.d1Pin && p.d1Pin !== p.phoneRuntimeVersion
                                  ? ` (d1=${p.d1Pin})`
                                  : ""
                              }`
                            : "";
                        return `${p.locale}@v${p.versionN}${stamp}${
                          p.previousPublishedVersionId
                            ? " (superseded prev)"
                            : ""
                        }`;
                      })
                      .join(", ")}
                  </p>
                ) : null}
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap">
                  {JSON.stringify(lastPublish.opapi, null, 2)}
                </pre>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>

      <AlertDialog
        open={publishOpen}
        onOpenChange={(open) => {
          if (publishMutation.isPending) return;
          if (!open) {
            setPublishOpen(false);
            resetPublishDialogOptions();
            return;
          }
          openPublishDialog();
        }}
      >
        <AlertDialogContent className="max-w-md sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>发布到生产环境？</AlertDialogTitle>
            <AlertDialogDescription>
              选择一种或多种语言。发布会跑就绪检查（除非开启强制）、写入手机运行时
              pin，并通过 opapi 将各语言线上包指向所选版本。各语言此前的生产包会被取代。
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="flex flex-col gap-4">
            <Field>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <FieldLabel>语言</FieldLabel>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={publishMutation.isPending}
                    onClick={() => setSelectedLocales([...localesWithPhone])}
                  >
                    全选
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={publishMutation.isPending}
                    onClick={() => setSelectedLocales([])}
                  >
                    清空
                  </Button>
                </div>
              </div>
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                {localesWithPhone.map((loc) => {
                  const meta = localeMetaByCode.get(loc);
                  const checked = selectedLocales.includes(loc);
                  return (
                    <label
                      key={loc}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                        "hover:bg-muted/60",
                        checked ? "bg-muted/40" : undefined,
                      )}
                    >
                      <input
                        type="checkbox"
                        className="size-4 shrink-0 rounded border border-input accent-primary"
                        checked={checked}
                        disabled={publishMutation.isPending}
                        onChange={(e) =>
                          setSelectedLocales((prev) =>
                            toggleLocale(prev, loc, e.target.checked),
                          )
                        }
                      />
                      <span className="min-w-10 font-medium">{loc}</span>
                      {meta?.latestVersionN != null ? (
                        <span className="text-xs text-muted-foreground">
                          v{meta.latestVersionN}
                          {meta.latestStatus
                            ? ` · ${formatVersionStatus(meta.latestStatus)}`
                            : ""}
                        </span>
                      ) : null}
                      {meta?.publishedVersionId ? (
                        <Badge variant="success" className="ml-auto">
                          线上
                        </Badge>
                      ) : (
                        <span className="ml-auto text-xs text-muted-foreground">
                          未上线
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <FieldDescription>
                {selectedLocales.length === 0
                  ? "请至少选择一种语言发布。"
                  : `将发布：${selectedLocales.join(", ")}。`}
              </FieldDescription>
            </Field>

            <Field>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <FieldLabel>发布范围（用户组）</FieldLabel>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={
                      publishMutation.isPending || userGroups.length === 0
                    }
                    onClick={() =>
                      setSelectedGroupIds(userGroups.map((g) => g.id))
                    }
                  >
                    全选
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={publishMutation.isPending}
                    onClick={() => setSelectedGroupIds([])}
                  >
                    清空
                  </Button>
                </div>
              </div>
              {groupsQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : userGroups.length === 0 ? (
                <p className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
                  暂无用户组。不选即全量发布。可在「用户组」页创建。
                </p>
              ) : (
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                  {userGroups.map((group) => {
                    const checked = selectedGroupIds.includes(group.id);
                    return (
                      <label
                        key={group.id}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                          "hover:bg-muted/60",
                          checked ? "bg-muted/40" : undefined,
                        )}
                      >
                        <input
                          type="checkbox"
                          className="size-4 shrink-0 rounded border border-input accent-primary"
                          checked={checked}
                          disabled={publishMutation.isPending}
                          onChange={(e) =>
                            setSelectedGroupIds((prev) =>
                              e.target.checked
                                ? prev.includes(group.id)
                                  ? prev
                                  : [...prev, group.id]
                                : prev.filter((id) => id !== group.id),
                            )
                          }
                        />
                        <span className="font-medium">{group.name}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {group.uidCount} UID
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              <FieldDescription>
                {selectedGroupIds.length === 0
                  ? "全量发布（不限制 UID）。"
                  : `定向发布：合并 ${selectedGroupIds.length} 个用户组，共 ${selectedGroupUidCount} 个 UID。`}
              </FieldDescription>
            </Field>

            <div className="flex flex-col gap-3">
              <Field orientation="horizontal" className="w-fit">
                <Switch
                  id="publish-force"
                  checked={forcePublish}
                  disabled={publishMutation.isPending}
                  onCheckedChange={setForcePublish}
                />
                <FieldLabel htmlFor="publish-force">强制发布</FieldLabel>
              </Field>
              <p className="text-xs text-muted-foreground">
                跳过就绪门禁。未完成的包也可能上线。
              </p>
              <Field orientation="horizontal" className="w-fit">
                <Switch
                  id="publish-as-latest"
                  checked={publishAsLatest}
                  disabled={publishMutation.isPending || !sourcePinIsLatest}
                  onCheckedChange={setPublishAsLatest}
                />
                <FieldLabel htmlFor="publish-as-latest">
                  以 latest 发布
                </FieldLabel>
              </Field>
              <p className="text-xs text-muted-foreground">
                仅当版本 pin 为浮动 latest 时可用。已发布 JSON 将携带字面 pin
                latest。
              </p>
              {publishAsLatest && sourcePinIsLatest ? (
                <Alert variant="destructive">
                  <AlertTitle>已开启「以 latest 发布」</AlertTitle>
                  <AlertDescription>
                    已发布 JSON 将携带字面 pin{" "}
                    <code className="text-xs">latest</code>
                    。玩家运行时跟随 CDN latest 指针。
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishMutation.isPending}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={publishConfirmDisabled}
              onClick={() => publishMutation.mutate()}
            >
              {publishMutation.isPending ? <Spinner /> : null}
              {forcePublish
                ? "强制发布为生产环境"
                : "发布为生产环境"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={unpublishOpen}
        onOpenChange={(open) => {
          if (unpublishMutation.isPending) return;
          if (!open) {
            setUnpublishOpen(false);
            return;
          }
          openUnpublishDialog();
        }}
      >
        <AlertDialogContent className="max-w-md sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>从生产环境取消发布？</AlertDialogTitle>
            <AlertDialogDescription>
              选择一种或多种线上语言，从生产环境移除。所选语言会从 OPAPI
              删除。版本回到草稿，之后可再次发布。
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Field>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <FieldLabel>语言</FieldLabel>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={unpublishMutation.isPending}
                  onClick={() =>
                    setSelectedUnpublishLocales(
                      liveLocales.map((l) => l.locale),
                    )
                  }
                >
                  全选
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={unpublishMutation.isPending}
                  onClick={() => setSelectedUnpublishLocales([])}
                >
                  清空
                </Button>
              </div>
            </div>
            <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
              {liveLocales.map((live) => {
                const meta = localeMetaByCode.get(live.locale);
                const checked = selectedUnpublishLocales.includes(live.locale);
                const liveVersion = versions.find((v) => v.id === live.versionId);
                return (
                  <label
                    key={live.locale}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                      "hover:bg-muted/60",
                      checked ? "bg-muted/40" : undefined,
                    )}
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 rounded border border-input accent-primary"
                      checked={checked}
                      disabled={unpublishMutation.isPending}
                      onChange={(e) =>
                        setSelectedUnpublishLocales((prev) =>
                          toggleLocale(prev, live.locale, e.target.checked),
                        )
                      }
                    />
                    <span className="min-w-10 font-medium">{live.locale}</span>
                    {liveVersion ? (
                      <span className="text-xs text-muted-foreground">
                        v{liveVersion.versionN}
                      </span>
                    ) : meta?.latestVersionN != null ? (
                      <span className="text-xs text-muted-foreground">
                        v{meta.latestVersionN}
                      </span>
                    ) : null}
                    <Badge variant="success" className="ml-auto">
                      线上
                    </Badge>
                  </label>
                );
              })}
            </div>
            <FieldDescription>
              {selectedUnpublishLocales.length === 0
                ? "请至少选择一种语言取消发布。"
                : `将取消发布：${selectedUnpublishLocales.join(", ")}。`}
            </FieldDescription>
          </Field>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishMutation.isPending}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={unpublishConfirmDisabled}
              onClick={() => unpublishMutation.mutate()}
            >
              {unpublishMutation.isPending ? <Spinner /> : null}
              取消发布
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
